import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Play, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CATEGORY_COLORS = {
  compliance: 'bg-amber-50 text-amber-700 border-amber-200',
  accounting: 'bg-blue-50 text-blue-700 border-blue-200',
  savings:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  risk:       'bg-red-50 text-red-700 border-red-200',
};

const ManageJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [editing, setEditing] = useState(null); // { job }
  const [editHour, setEditHour] = useState(0);
  const [editMinute, setEditMinute] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/jobs');
      setJobs(res.data);
    } catch (e) { toast.error('Failed to load jobs'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (job) => {
    try {
      await api.put(`/jobs/${job.job_id}`, { is_active: !job.is_active });
      toast.success(`${job.name} ${!job.is_active ? 'enabled' : 'disabled'}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const runNow = async (job) => {
    setRunning(job.job_id);
    try {
      const res = await api.post(`/jobs/${job.job_id}/run-now`);
      toast.success(`Job ran with status: ${res.data.status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    setRunning(null);
  };

  const openEdit = (job) => {
    setEditing(job);
    setEditHour(job.schedule?.hour ?? 0);
    setEditMinute(job.schedule?.minute ?? 0);
  };

  const saveSchedule = async () => {
    try {
      await api.put(`/jobs/${editing.job_id}`, { schedule: { hour: editHour, minute: editMinute } });
      toast.success('Schedule updated');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <AdminSubPage title="Manage Jobs" breadcrumb={['Admin', 'System', 'Manage Jobs']} backPath="/dashboard/admin/system">
      <div className="bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-xs text-blue-800">
        <strong>What lives here:</strong> Every periodic batch job in the system. Toggle a job off to skip its next cron run; toggle on to resume. <em>Run Now</em> triggers the job immediately and persists the report. All times are <strong>UTC</strong> (SAST = UTC+2).
      </div>
      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="jobs-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Job</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Category</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Schedule (UTC)</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Last Run</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Next Run</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            jobs.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">No jobs registered.</td></tr> :
            jobs.map(j => (
              <tr key={j.job_id} className="border-b hover:bg-gray-50" data-testid={`job-row-${j.job_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-sm text-[#1f1f1f]">{j.name}</div>
                  <div className="text-xs text-gray-400 max-w-md">{j.description}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${CATEGORY_COLORS[j.category] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{j.category}</span>
                </td>
                <td className="px-4 py-3 text-xs font-mono">{String(j.schedule?.hour ?? 0).padStart(2,'0')}:{String(j.schedule?.minute ?? 0).padStart(2,'0')}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {j.last_run ? <>
                    <div>{j.last_run.slice(0, 16).replace('T',' ')}</div>
                    <div className={`text-[10px] uppercase ${j.last_run_status === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{j.last_run_status}</div>
                  </> : <span className="text-gray-400">never</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{j.next_run ? j.next_run.slice(0, 16).replace('T',' ') : '—'}</td>
                <td className="px-4 py-3 text-center"><Switch checked={!!j.is_active} onCheckedChange={() => toggleActive(j)} data-testid={`job-toggle-${j.job_id}`} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(j)} title="Edit schedule" data-testid={`job-edit-${j.job_id}`}><SettingsIcon className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => runNow(j)} disabled={running === j.job_id} title="Run now" className="text-blue-600" data-testid={`job-run-${j.job_id}`}>
                      {running === j.job_id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" aria-describedby="job-edit-desc">
          <DialogHeader><DialogTitle>Edit schedule — {editing?.name}</DialogTitle></DialogHeader>
          <p id="job-edit-desc" className="sr-only">Update cron schedule (UTC)</p>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">{editing?.description}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Hour (0–23 UTC)</Label><Input type="number" min={0} max={23} value={editHour} onChange={e => setEditHour(parseInt(e.target.value || 0))} data-testid="job-edit-hour" /></div>
              <div><Label className="text-xs">Minute (0–59)</Label><Input type="number" min={0} max={59} value={editMinute} onChange={e => setEditMinute(parseInt(e.target.value || 0))} data-testid="job-edit-minute" /></div>
            </div>
            <p className="text-[10px] text-gray-400">SAST = UTC+2. e.g. UTC 00:30 ≈ SAST 02:30.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={saveSchedule} className="bg-[#1f1f1f] text-white" data-testid="job-edit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};

export default ManageJobsPage;
